import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { VideoCard } from '../components/VideoCard';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp, Calendar, Radio, Disc3, Music2, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;
const EYE_ICON_URL = "/sidebar-logo.png";

const MOCK_THUMBNAILS = [
  "https://images.unsplash.com/photo-1762279801044-3c37872f4534?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/28583190/pexels-photo-28583190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1764258906659-e966a089ed7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1722708773274-e791ab361dd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxjeWJlcnB1bmslMjBmdXR1cmlzdGljJTIwZGolMjBjb25jZXJ0fGVufDB8fHx8MTc3NTEzNzM2Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1741344694428-f84a93bc4eca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxjeWJlcnB1bmslMjBmdXR1cmlzdGljJTIwZGolMjBjb25jZXJ0fGVufDB8fHx8MTc3NTEzNzM2Nnww&ixlib=rb-4.1.0&q=85"
];

export const DiscoverPage = () => {
  const [activeTab, setActiveTab] = useState('for-you');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [tools, setTools] = useState([]);
  const [selectedTool, setSelectedTool] = useState(null);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const { axiosInstance, user } = useAuth();
  const { playVideo } = usePlayer();
  const navigate = useNavigate();

  const tabs = [
    { id: 'for-you', label: 'For You', icon: Sparkles, description: 'Personalized just for you' },
    { id: 'weekly', label: 'New Releases', icon: Calendar, description: 'Fresh drops this week' },
    { id: 'trending', label: 'Trending', icon: TrendingUp, description: 'What\'s hot right now' },
    { id: 'daily-mix', label: 'Daily Mix', icon: Radio, description: 'Your daily personalized mix' },
  ];

  useEffect(() => {
    fetchVideos();
    fetchGenres();
    fetchTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedGenre, selectedTool]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      // Tool filter overrides the tab — it's a hard filter on a specific tool
      if (selectedTool) {
        const res = await axiosInstance.get('/videos', { params: { tool: selectedTool, limit: 40 } });
        setVideos(res.data);
        return;
      }
      let endpoint = `/discover/${activeTab}`;
      if (selectedGenre) {
        endpoint = `/discover/genre/${selectedGenre}`;
      }
      const res = await axiosInstance.get(endpoint, { params: { limit: 20 } });
      setVideos(res.data);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const res = await axiosInstance.get('/genres');
      setGenres(res.data);
    } catch (err) {
      console.error('Failed to fetch genres:', err);
    }
  };

  const fetchTools = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/tools');
      setTools((res.data?.tools || []).slice(0, 20));
    } catch {
      setTools([]);
    }
  }, [axiosInstance]);

  // Featured marquee (Quality Gate) — pinned at the top of Discover so admin-
  // approved videos always get top placement, regardless of algorithm.
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await axiosInstance.get('/featured?limit=8');
        setFeaturedVideos(res.data?.videos || []);
      } catch {
        setFeaturedVideos([]);
      }
    };
    fetchFeatured();
  }, [axiosInstance]);

  const getThumbnail = (video) => {
    // Single source of truth → AUDIVUE branded placeholder when nothing else
    // resolves (no per-page stock photo fallback that could mask a missing
    // custom_thumbnail_url).
    return resolveThumbnailUrl(video);
  };

  const handleRefresh = () => {
    setSelectedGenre(null);
    setSelectedTool(null);
    fetchVideos();
  };

  const activeTabData = tabs.find(t => t.id === activeTab);

  const goToArtist = (video) => {
    const artistId = video.artist_id || video.user_id;
    if (artistId) {
      navigate(`/creator/${artistId}`);
    }
  };

  return (
    <div className="pb-32">
      {/* Hero Section */}
      <section className="relative overflow-hidden mb-8">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-800/20 via-transparent to-transparent pointer-events-none" />
        
        <div className="relative z-10 py-10 px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <img src={EYE_ICON_URL} alt="AUDIVUE" className="w-12 h-12 rounded-xl shadow-lg" />
              <span className="text-zinc-400 font-medium text-lg">Discover</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-2">
              <span className="text-white">Find Your Sound</span>
            </h1>
            <p className="text-zinc-400 text-lg max-w-xl">
              Personalized picks, tuned to you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured marquee — pinned admin-approved videos (Featured Quality Gate). Hidden when filtering. */}
      {featuredVideos.length > 0 && !selectedGenre && !selectedTool && (
        <section className="px-6 mb-8" data-testid="discover-featured-marquee">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-fuchsia-400" />
              <span className="text-sm text-zinc-400 font-medium">Featured</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {featuredVideos.slice(0, 4).map((video, index) => (
              <VideoCard
                key={`featured-${video.id}`}
                video={video}
                thumbnail={getThumbnail(video, index)}
                onClick={() => playVideo(video, featuredVideos, index)}
                onArtistClick={goToArtist}
              />
            ))}
          </div>
        </section>
      )}

      {/* Tab row removed per Feb 2026 spec — Discover is now signed-in only
          and renders the personalized For You feed by default. The tab
          component is intentionally retained in this codebase but commented
          out so future product decisions (e.g. re-enabling Daily Mix as its
          own surface) don't have to re-build the wiring. */}
      {/*
      <section className="px-6 mb-8">
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedGenre(null);
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full whitespace-nowrap transition-all ${
                activeTab === tab.id && !selectedGenre
                  ? 'bg-white text-black shadow-lg'
                  : 'bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </div>
      </section>
      */}

      {/* Genre Pills */}
      {genres.length > 0 && (
        <section className="px-6 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Music2 className="w-4 h-4 text-zinc-500" />
            <span className="text-sm text-zinc-500 font-medium">Browse by Genre</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {genres.map((genre) => (
              <Button
                key={genre}
                onClick={() => {
                  setSelectedGenre(selectedGenre === genre ? null : genre);
                  setSelectedTool(null);
                }}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
                  selectedGenre === genre
                    ? 'bg-white text-black'
                    : 'bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
                data-testid={`genre-${genre.toLowerCase()}`}
              >
                {genre}
              </Button>
            ))}
          </div>
        </section>
      )}

      {/* AI Tool Pills — filter by disclosed AI tool */}
      {tools.length > 0 && (
        <section className="px-6 mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-zinc-500" />
              <span className="text-sm text-zinc-500 font-medium">Filter by AI tool</span>
            </div>
            <button
              onClick={() => navigate('/tools')}
              className="text-xs text-zinc-500 hover:text-white"
              data-testid="see-all-tools-btn"
            >
              See all →
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {tools.map((t) => (
              <Button
                key={t.key}
                onClick={() => {
                  setSelectedTool(selectedTool === t.key ? null : t.key);
                  setSelectedGenre(null);
                }}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all flex items-center gap-1.5 ${
                  selectedTool === t.key
                    ? 'bg-white text-black'
                    : 'bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
                data-testid={`tool-filter-${t.key}`}
              >
                <Sparkles className="w-3 h-3" />
                {t.name}
                <span className={`text-[10px] font-mono ${selectedTool === t.key ? 'opacity-60' : 'opacity-50'}`}>
                  {t.video_count}
                </span>
              </Button>
            ))}
          </div>
        </section>
      )}

      {/* Content Header */}
      <section className="px-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {activeTabData && (
              <>
                <div className="p-2 rounded-lg bg-zinc-800/60">
                  <activeTabData.icon className="w-5 h-5 text-zinc-300" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    {selectedTool ? `Made with ${tools.find(t => t.key === selectedTool)?.name || selectedTool}` : selectedGenre ? `${selectedGenre} Mix` : activeTabData.label}
                  </h2>
                  <p className="text-zinc-500 text-sm">
                    {selectedTool ? 'AI videos using this tool' : selectedGenre ? `Top ${selectedGenre} videos` : activeTabData.description}
                  </p>
                </div>
              </>
            )}
          </div>
          
          <Button
            onClick={handleRefresh}
            variant="ghost"
            className="text-zinc-400 hover:text-white"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </section>

      {/* Video Grid */}
      <section className="px-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-video bg-zinc-900 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl text-zinc-400 mb-2">No videos found</h3>
            <p className="text-zinc-500">
              {user 
                ? 'Like some videos to get personalized recommendations!'
                : 'Sign in to get personalized recommendations'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {videos.map((video, index) => (
              <VideoCard
                key={video.id}
                video={video}
                thumbnail={getThumbnail(video, index)}
                onClick={() => playVideo(video, videos, index)}
                onArtistClick={goToArtist}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
